import { useCallback, useEffect, useRef, useState } from "react";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService";
import { CommandGetJob } from "@services/jobs/commands/cmdGetJob";
import { launchActions } from "src/modules/engagement/launchStore";
import type { CalculateAudienceResponse } from "@services/engagements/launch/audience/commands/cmdCalculateAudience";

const TERMINAL_STATUSES = ["COMPLETED", "FAILED", "COMPLETED_WITH_ERRORS"];
const POLL_MS = 1500;

/*
    Calculating an audience materializes its members from its rules. It runs as
    a background job, except under CELERY_TASK_ALWAYS_EAGER where it finishes
    before the response returns — so check for a terminal status before polling.
*/
export function useAudienceCalculation() {
  const [calculatingId, setCalculatingId] = useState<number | null>(null);
  const [resultById, setResultById] = useState<Map<number, CalculateAudienceResponse>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const onJobComplete = useCallback((job: any, audienceId: number) => {
    setCalculatingId(null);
    if (job.status === "FAILED") {
      setError("Audience calculation failed.");
      return;
    }
    const output = job.job_context?.output;
    if (output) setResultById((prev) => new Map(prev).set(audienceId, output));
    launchActions.triggerAudienceRefresh();
  }, []);

  const calculate = useCallback(
    async (audienceId: number, audienceName: string) => {
      setCalculatingId(audienceId);
      setError(null);
      stopPolling();

      try {
        const job = await new AudienceDefinitionService().calculateAudience(audienceId);
        if (TERMINAL_STATUSES.includes(job.status)) {
          onJobComplete(job, audienceId);
          return;
        }
        const cmd = new CommandGetJob();
        pollRef.current = setInterval(async () => {
          try {
            const updated = await cmd.run(job.id);
            if (!TERMINAL_STATUSES.includes(updated.status)) return;
            stopPolling();
            onJobComplete(updated, audienceId);
          } catch {
            stopPolling();
            setCalculatingId(null);
            setError(`Could not check calculation status for ${audienceName}.`);
          }
        }, POLL_MS);
      } catch {
        setCalculatingId(null);
        setError(`Could not calculate ${audienceName}.`);
      }
    },
    [onJobComplete, stopPolling],
  );

  return { calculate, calculatingId, resultById, error, setError };
}
